import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  type SecureFileSelectionRequest,
  useLazyGetAllSecureFilesQuery,
} from "features/secureUploads/secureUploads";

import type { FileTableSelection } from "./SecureFileTable";
import {
  MAX_BULK_FILES,
  drop,
  emptySelection,
  pageAfterRemoval,
  pageCheck,
  refresh,
  selectAll,
  selected,
  selectionLabel,
  toggle,
  togglePage,
} from "./selectionModel";

/** Files picked on a paged list, across its pages: the boxes of the table, the
 * bar's label and "Select all", and what happens when files leave the list.
 * Another search is another list - what was picked belonged to the old one. */
export function useFileSelection({
  rows,
  search,
  fileTotal,
  rowTotal,
  page,
  limit,
  setPage,
  error,
  everything,
}: {
  /** The files on screen (collections never take a box). */
  rows: SecureFile[] | undefined;
  search: string;
  /** Every file of the list: the label, "Select all" and the bulk limit. */
  fileTotal: number;
  /** Every row the pages run over (collections first on a project page). */
  rowTotal: number;
  page: number;
  limit: number;
  setPage: Dispatch<SetStateAction<number>>;
  /** The list's own error: past the last page it answers 404. */
  error: unknown;
  /** Every file of the list as it is searched now, for "Select all". */
  everything: SecureFileSelectionRequest;
}) {
  const dispatch = useDispatch();
  const [selection, setSelection] = useState(() => emptySelection());
  const [announcement, setAnnouncement] = useState("");
  const [fetchAll, { isFetching: selectingAll }] =
    useLazyGetAllSecureFilesQuery();

  useEffect(() => setSelection(emptySelection(search)), [search]);
  // Rows on screen bring fresher flags (who may delete what) to the picked files.
  useEffect(() => {
    if (rows) setSelection((current) => refresh(current, rows));
  }, [rows]);
  // Past the last page the list answers 404 and the table kept the old rows.
  useEffect(() => {
    if ((error as { status?: number } | undefined)?.status === 404 && page > 1)
      setPage(1);
  }, [error, page, setPage]);

  const picked = selected(selection);
  const count = picked.length;
  const shown = useRef(0);
  useEffect(() => {
    setAnnouncement(
      count
        ? selectionLabel(count, fileTotal)
        : shown.current
          ? "Selection cleared"
          : "",
    );
    shown.current = count;
  }, [count, fileTotal]);

  /** Files that left the list: out of the selection, and a page that still is. */
  const removed = (ids: number[]) => {
    setSelection((current) => drop(current, ids));
    setPage((current) =>
      pageAfterRemoval(current, limit, rowTotal, ids.length),
    );
  };

  const clear = () => setSelection(emptySelection(search));

  const takeAll = async () => {
    const scope = search;
    try {
      const all = await fetchAll(everything).unwrap();
      setSelection((current) => selectAll(current, all, scope));
    } catch {
      dispatch(
        pushNotification({
          message: "The files could not be selected.",
          severity: "error",
        }),
      );
    }
  };

  const pageRows = rows ?? [];
  const table: FileTableSelection = {
    isSelected: (id) => Boolean(selection.byId[id]),
    onToggle: (file, on) =>
      setSelection((current) => toggle(current, file, on)),
    onTogglePage: (on) =>
      setSelection((current) => togglePage(current, pageRows, on)),
    page: pageCheck(selection, pageRows),
  };

  return {
    picked,
    count,
    announcement,
    label: selectionLabel(count, fileTotal),
    /** "Select all N files": there is more to take, within the bulk limit. */
    canTakeAll: count < fileTotal && fileTotal <= MAX_BULK_FILES,
    selectingAll,
    takeAll,
    removed,
    clear,
    table,
  };
}
